import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { createEmailValidationSchema } from '@dhl/inputs'
import { useCreateAddressValidationSchema } from '@dhl/input-address'
import type { TranslationContext } from '~/hooks'
import type { LabelType, ReturnRequest, ReturnType, TransportType } from '~/model'
import { ServiceCarrier } from '~/app-constants'

export const useCreateReturnRequestValidationSchema = (
  translationContext: TranslationContext,
  countryCode: string
): Yup.ObjectSchema<ReturnRequest> => {
  const { t } = useTranslation()

  const requiredMessage = t('inputs.name.fieldIsRequiredErrorMessage')
  const requiredNumber = Yup.number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .required(requiredMessage)
  const requiredPickupDate = Yup.string().when('returnType', {
    is: (returnType: ReturnType) => returnType === 'consumer_pickup' || returnType === 'pickup',
    then: (schema) => schema.required(requiredMessage),
  })
  const isCcc = translationContext.context === 'ccc'
  const referenceInputLength = isCcc ? 15 : 10
  const addressValidationSchema = useCreateAddressValidationSchema(ServiceCarrier, countryCode)

  // @ts-expect-error
  return Yup.object({
    address: addressValidationSchema,
    companyName: Yup.string().when('returnType', {
      is: (returnType: ReturnType) => returnType === 'pickup',
      then: (schema) => schema.required(requiredMessage),
    }),
    firstName: Yup.string().required(requiredMessage),
    lastName: Yup.string().required(requiredMessage),
    email: createEmailValidationSchema(t),
    reference: Yup.string()
      .required(requiredMessage)
      .max(referenceInputLength, t('inputs.reference.fieldMaxLength', { length: referenceInputLength.toString() })),

    transportType: Yup.mixed<TransportType>().required(requiredMessage),
    returnType: Yup.mixed<ReturnType>().required(requiredMessage),
    returnDeclarationMechanism: Yup.mixed<LabelType>().optional(),

    pickupDate: requiredPickupDate,
    pickupTimeWindowStart: Yup.string(),
    pickupTimeWindowEnd: Yup.string(),
    pallets: Yup.number()
      .min(0, t('inputs.number.higherThanZero'))
      .max(5, t('inputs.number.lowerThanSix'))
      .when('returnType', {
        is: (returnType: ReturnType) => returnType === 'pickup',
        then: () => requiredNumber,
      }),
    packages: Yup.number()
      .min(0, t('inputs.number.higherThanZero'))
      .when('pallets', {
        is: 0,
        then: (schema) => schema.min(1, t('inputs.number.shouldBeAtLeastOne')),
      })
      .when('returnType', {
        is: (returnType: ReturnType) => returnType === 'pickup',
        then: () => requiredNumber,
      }),
    packageSizeConfirmed: Yup.bool()
      .oneOf([true], t('inputs.packageSizeConfirmed.fieldIsRequiredErrorMessage'))
      .required(),
    termsConfirmed: Yup.bool()
      .oneOf([true], t('inputs.termsConfirmed.fieldIsRequiredErrorMessage', translationContext))
      .required(),
    captcha: Yup.string()
      .transform((value) => value || '')
      .required(requiredMessage),
  })
}
